import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { environment } from './../../../../environments/environment';
import { DndPortalPoint } from "./../../../shared/lib/api-constant";
import { CarrierResponse, CommonApiResponse, CompanyResponse, ContainerResponse, CurrencyInfo, CurrencyResponse, PortHierarchyResponse, PortResponse } from '../../lib/model/common';

@Injectable({
  providedIn: 'root',
})
export class ContractsCommonService {
  private useMockData = false; // Set to `false` when using actual API
  static sharedData: any = {};

  private containerTypesSubject = new BehaviorSubject<ContainerResponse | null>(null);
  public containerTypes$ = this.containerTypesSubject.asObservable(); // Exposed observable for components

  private portUnlocSubject = new BehaviorSubject<PortResponse | null>(null);
  public portUnloc$ = this.portUnlocSubject.asObservable(); // Exposed observable for components

  private companySubject = new BehaviorSubject<CompanyResponse | null>(null);
  public company$ = this.companySubject.asObservable(); // Exposed observable for components

  private carrierSubject = new BehaviorSubject<CarrierResponse | null>(null);
  public carrier$ = this.carrierSubject.asObservable(); // Exposed observable for components

  constructor(private http: HttpClient) { }

  //#region Common Methods

  /**
   * Fetches all ports.
   * Returns mock data if `useMockData` is `true`.
   * @returns Observable<any>
   */
  getAllPorts(): Observable<PortResponse> {
    return this.http.get<PortResponse>(DndPortalPoint.DndCommonEndppoint.PORTS).pipe(
      tap((response: any) => {
        if (response && response.data) {
          this.portUnlocSubject.next(response.data)
        }
      })
    );
  }

  getPortUnlocSync(): any {
    return this.portUnlocSubject.value;
  }

  /**
   * Fetches all container types.
   * Returns mock data if `useMockData` is `true`.
   * @returns Observable<any>
   */
  getAllContainerTypes(): Observable<ContainerResponse> {
    return this.http.get<ContainerResponse>(DndPortalPoint.DndCommonEndppoint.CONTAINER).pipe(
      tap((response: any) => {
        if (response && response.data) {
          this.containerTypesSubject.next(response.data)
        }
      })
    );
  }

  getContainerTypesSync(): ContainerResponse | null {
    return this.containerTypesSubject.value;
  }

  /**
   * Fetches all companies by the user.
   * Returns mock data if `useMockData` is `true`.
   * @returns Observable<any>
   */
  getAllCompaniesByUser(): Observable<CompanyResponse> {
    return this.http.get<CompanyResponse>(DndPortalPoint.DndCommonEndppoint.COMPANIES).pipe(
      tap((response: any) => {
        if (response && response.data) {
          this.companySubject.next(response.data)
        }
      })
    );
  }

  getAllCompanies(): Observable<CompanyResponse> {
    return this.http.get<CompanyResponse>(DndPortalPoint.DndCommonEndppoint.ALL_COMPANIES);
  }

  getCompanySync(): CompanyResponse | null {
    return this.companySubject.value;
  }

  getPortHierarchy(): Observable<PortHierarchyResponse> {
    return this.http.get<PortHierarchyResponse>(DndPortalPoint.DndCommonEndppoint.PORT_LIST);
  }

  /**
   * Fetches all carriers.
   * Returns mock data if `useMockData` is `true`.
   * @returns Observable<any>
   */
  getAllCarriers(): Observable<CarrierResponse> {
    return this.http.get<CarrierResponse>(DndPortalPoint.DndCommonEndppoint.CARRIER).pipe(
      tap((response: any) => {
        if (response && response.data) {
          this.carrierSubject.next(response.data)
        }
      })
    );
  }

  getCarrierSync(): CarrierResponse | null {
    return this.carrierSubject.value;
  }

  /**
   * Fetches all currency list.
   * Returns mock data if `useMockData` is `true`.
   * @returns Observable<any>
   */
  getAllCurrencyList(): Observable<CurrencyResponse> {
    return this.http.get<CurrencyResponse>(DndPortalPoint.DndCommonEndppoint.CURRENCY);
  }

  //#endregion

  //#region Contract Creation Methods

  /**
   * Saves or updates a contract.
   *
   * @param datcontractRequest - The request payload containing contract data.
   * @returns An Observable of the HTTP response from the save or update contract endpoint.
   */
  saveOrUpdateContract(datcontractRequest: any) {
    return this.http.post(DndPortalPoint.DndContractCreationPoint.SAVE_UPDATE_CONTRACT, datcontractRequest);
  }

  /**
   * Saves a contract as a draft.
   *
   * @param datcontractRequest - The request payload containing contract data.
   * @returns An Observable of the HTTP response from the save as draft
   */
  saveContractAsDraft(datacontractRequest: any) {
    return this.http.post(DndPortalPoint.DndContractCreationPoint.SAVE_AS_DRAFT, datacontractRequest);
  }

  getContractDetailsFromStatus(page: number, size: number, status: number, filterRequest: {}): Observable<CommonApiResponse> {
    const params = new HttpParams()
      // .set('page', page.toString())
      // .set('size', size.toString())
      .set('status', status.toString());
    return this.http.post<CommonApiResponse>(
      DndPortalPoint.DndContractCreationPoint.GET_CONTRACT_DETAILS_FROM_STATUS,
      filterRequest,
      { params }
    );
  }

  downloadContract(status: number, filterRequest: {}): Observable<any> {
    const params = new HttpParams()
      .set('status', status.toString());
    return this.http.post<any>(
      DndPortalPoint.DndContractCreationPoint.DOWNLOAD_CONTRACT,
      filterRequest,
      { params }
    );
  }


  getContractDrafts(request: {}, page: number, size: number): Observable<CommonApiResponse> {
    // const params = new HttpParams()
    //   .set('page', page.toString())
    //   .set('size', size.toString());
    return this.http.post<CommonApiResponse>(DndPortalPoint.DndContractCreationPoint.GET_CONTRACT_DRAFTS,
      // { params }, 
      request);
  }

  getDetailedLogs(contractId: string): Observable<any> {
    let params = new HttpParams().set('contractId', contractId);
    return this.http.get(DndPortalPoint.DndContractCreationPoint.GET_DETAILED_LOGS, { params: params });
  }

  //#endregion

  //#region Approval Contracts Methods

  /**
    * Approves a contract.
    *
    * @param contractId - The ID of the contract to approve.
    * @returns An Observable of the HTTP response from the approve contract endpoint.
    */
  approveOrRejectContracts(contractActionBody: {}): Observable<any> {
    return this.http.post(DndPortalPoint.DndContractApprovalPoint.APPROVE_REJECT_CONTRACT, contractActionBody);
  }
  /**
   * Fetches all contracts for approval.
   * Returns mock data if `useMockData` is `true`.
   * @returns Observable<any>
   */
  getContractsForApproval(filterRequest: any, page: number, size: number): Observable<any> {
    // const params = new HttpParams()
    //   .set('page', page.toString())
    //   .set('size', size.toString());
    return this.http.post(DndPortalPoint.DndContractApprovalPoint.GET_CONTRACTS, filterRequest
      //, { params }
    );
  }

  //#endregion


  /**
   * Uploads a contract CSV file.
   *
   * @param file - The file to upload.
   * @returns An Observable of the HTTP response from the upload contract CSV endpoint.
   */
  public uploadFile(file: File): Observable<any> {
    let body = new FormData();
    body.append("contractsCsvFile", file);
    return this.http.post(DndPortalPoint.DndContractUploadPoint.UPLOAD_CONTRACT_CSV, body);
  }


  /**
   * Fetches the history of uploaded contracts.
   * Returns mock data if `useMockData` is `true`.
   * @returns Observable<any>
   */
  public getContractUploadHistory(): Observable<any> {
    return this.http.get(DndPortalPoint.DndContractUploadPoint.GET_CSV_HISTORY);
  }

  public downloadErrorLog(fileId: string): Observable<any> {
    return this.http.get(DndPortalPoint.DndContractUploadPoint.DOWNLOAD_ERROR_LOG + '/' + fileId, { responseType: 'blob' });
  }

  public downloadCsvFile(fileId: string): Observable<any> {
    return this.http.get(DndPortalPoint.DndContractUploadPoint.DOWNLOAD_CSV_FILE + '/' + fileId, { responseType: 'blob' });
  }



}
